import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Body1, Button, Caption1, Input, Select, Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowClockwise24Regular, Search20Regular } from "@fluentui/react-icons";
import { useData } from "../../data/DataProvider";
import { FILTRI_CODA, loadCoda, ordinaCoda, type FiltroCoda, type Richiesta } from "../../data/richieste";
import { useConfigurazione } from "../../data/configurazione";
import { navigate } from "../../lib/nav";
import { storage } from "../../lib/storage";
import { Shell } from "../../components/Shell";
import { ChipGroup } from "../../components/ChipGroup";
import { RichiestaCard } from "../../components/RichiestaCard";
import { ErrorState } from "../../components/ErrorState";

const useStyles = makeStyles({
  filters: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS },
  select: { maxWidth: "320px" },
  count: { color: tokens.colorNeutralForeground3 },
  empty: { textAlign: "center", color: tokens.colorNeutralForeground2, padding: tokens.spacingVerticalXXL },
});

const FILTRI = Object.keys(FILTRI_CODA) as FiltroCoda[];

export function Coda({ switcher }: { switcher?: ReactNode }) {
  const s = useStyles();
  const data = useData();
  const [filtro, setFiltro] = useState<FiltroCoda>(() => (storage.get("filtroCoda") as FiltroCoda) ?? "Da gestire");
  const [sede, setSede] = useState<string>(() => storage.get("sedeCoda") ?? "");
  const { config } = useConfigurazione(data);
  const [testo, setTesto] = useState("");
  const [items, setItems] = useState<Richiesta[]>();
  const [nextLink, setNextLink] = useState<string>();
  const [error, setError] = useState<unknown>();

  const load = useCallback(async () => {
    setError(undefined);
    setItems(undefined);
    try {
      const page = await loadCoda(data, filtro, sede || undefined);
      setItems(page.items);
      setNextLink(page.nextLink);
    } catch (e) {
      setError(e);
    }
  }, [data, filtro, sede]);

  useEffect(() => {
    void load();
  }, [load]);

  const more = async () => {
    const page = await loadCoda(data, filtro, sede || undefined, nextLink);
    setItems((p) => [...(p ?? []), ...page.items]);
    setNextLink(page.nextLink);
  };

  const visibili = useMemo(() => {
    if (!items) return undefined;
    const t = testo.trim().toLowerCase();
    const filtered = t
      ? items.filter((r) =>
          [r.fields.Title, r.fields.Sede, r.fields.Struttura, r.fields.Servizio, r.fields.Area, r.fields.Categoria, r.createdBy?.displayName, String(r.id)]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(t)),
        )
      : items;
    return ordinaCoda(filtered);
  }, [items, testo]);

  return (
    <Shell
      title="Richieste di manutenzione"
      actions={
        <>
          {switcher}
          <Button appearance="subtle" icon={<ArrowClockwise24Regular />} aria-label="Aggiorna" onClick={load} />
        </>
      }
    >
      <div className={s.filters}>
        <ChipGroup
          ariaLabel="Filtro stato"
          options={FILTRI}
          value={filtro}
          onChange={(f) => {
            setFiltro(f);
            storage.set("filtroCoda", f);
          }}
        />
        <Select
          className={s.select}
          aria-label="Sede"
          value={sede}
          onChange={(_, d) => {
            setSede(d.value);
            storage.set("sedeCoda", d.value);
          }}
        >
          <option value="">Tutte le sedi</option>
          {(config?.sedi ?? (sede ? [sede] : [])).map((x) => (
            <option key={x} value={x}>
              {x}
            </option>
          ))}
        </Select>
        <Input
          size="large"
          contentBefore={<Search20Regular />}
          placeholder="Cerca per titolo, servizio, area, richiedente…"
          value={testo}
          onChange={(_, d) => setTesto(d.value)}
        />
      </div>

      {error ? (
        <ErrorState error={error} />
      ) : !visibili ? (
        <Spinner style={{ marginTop: 48 }} />
      ) : visibili.length === 0 ? (
        <Body1 className={s.empty}>{testo ? "Nessuna richiesta corrisponde alla ricerca." : "Nessuna richiesta in questo stato."}</Body1>
      ) : (
        <>
          <Caption1 className={s.count}>
            {visibili.length} {visibili.length === 1 ? "richiesta" : "richieste"}
            {nextLink ? " caricate" : ""}
          </Caption1>
          {visibili.map((r) => (
            <RichiestaCard key={r.id} r={r} showRichiedente onOpen={() => navigate(`/r/${r.id}`)} />
          ))}
          {nextLink && <Button onClick={more}>Mostra altre</Button>}
        </>
      )}
    </Shell>
  );
}
